/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { CustomerVoiceEntity } from "@cxp-shared/metadata-constants";
import { DataSources } from "../Constants/DataSources";
import { PlaceholderGenerator } from "../PlaceholderGenerator";
import { PlaceholderUtils } from "../Utilities/PlaceholderUtils";
var CustomerVoicePlaceholderGenerator = /** @class */ (function () {
    function CustomerVoicePlaceholderGenerator(odataServiceClient, urlBuilderFactory) {
        this.odataServiceClient = odataServiceClient;
        this.urlBuilderFactory = urlBuilderFactory;
    }
    CustomerVoicePlaceholderGenerator.prototype.generateAnonymousSurveyLinkPlaceholder = function (surveyId) {
        return __awaiter(this, void 0, void 0, function () {
            var surveyRecord;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getSurveyRecord(surveyId)];
                    case 1:
                        surveyRecord = _a.sent();
                        return [2 /*return*/, CustomerVoicePlaceholderGenerator.generateAnonymousSurveyLinkPlaceholderFromRecord(surveyId, surveyRecord.msfp_sourcesurveyidentifier, surveyRecord.msfp_name, surveyRecord.msfp_surveyurl)];
                }
            });
        });
    };
    CustomerVoicePlaceholderGenerator.prototype.generateNonAnonymousSurveyLinkPlaceholder = function (surveyId) {
        return __awaiter(this, void 0, void 0, function () {
            var surveyRecord;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getSurveyRecord(surveyId)];
                    case 1:
                        surveyRecord = _a.sent();
                        return [2 /*return*/, CustomerVoicePlaceholderGenerator.generateNonAnonymousSurveyLinkPlaceholderFromRecord(surveyId, surveyRecord.msfp_sourcesurveyidentifier, surveyRecord.msfp_name, surveyRecord.msfp_surveyurl)];
                }
            });
        });
    };
    CustomerVoicePlaceholderGenerator.generateAnonymousSurveyLinkPlaceholderFromRecord = function (surveyId, msfp_sourcesurveyidentifier, msfp_name, msfp_surveyurl) {
        return CustomerVoicePlaceholderGenerator.generateCustomerVoicePlaceholderWithBindingInputParameters(surveyId, msfp_sourcesurveyidentifier, msfp_name, msfp_surveyurl, DataSources.AnonymousSurveySource);
    };
    CustomerVoicePlaceholderGenerator.generateNonAnonymousSurveyLinkPlaceholderFromRecord = function (surveyId, msfp_sourcesurveyidentifier, msfp_name, msfp_surveyurl) {
        return CustomerVoicePlaceholderGenerator.generateCustomerVoicePlaceholderWithBindingInputParameters(surveyId, msfp_sourcesurveyidentifier, msfp_name, msfp_surveyurl, DataSources.NonAnonymousSurveySource);
    };
    CustomerVoicePlaceholderGenerator.generateNewPlaceholderKey = function (customerVoicePlaceholder, placeholders) {
        return PlaceholderUtils.createOrGetPlaceholderLabel(customerVoicePlaceholder, placeholders, function (number) { return "survey_".concat(number); }).placeholderKey;
    };
    CustomerVoicePlaceholderGenerator.prototype.getSurveyRecord = function (surveyId) {
        return __awaiter(this, void 0, void 0, function () {
            var entityRecordUrlBuilder, rawData;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        entityRecordUrlBuilder = this.urlBuilderFactory.createEntityRecordUrlBuilder(CustomerVoiceEntity.EntitySetName);
                        entityRecordUrlBuilder.setSelectedFields([
                            CustomerVoiceEntity.FieldName_SourceSurveyIdentifier,
                            CustomerVoiceEntity.FieldName_Name,
                            CustomerVoiceEntity.FieldName_SurveyUrl
                        ]);
                        entityRecordUrlBuilder.setRecordId(surveyId);
                        return [4 /*yield*/, this.odataServiceClient.getData(entityRecordUrlBuilder.build())];
                    case 1:
                        rawData = _a.sent();
                        return [2 /*return*/, JSON.parse(rawData)];
                }
            });
        });
    };
    CustomerVoicePlaceholderGenerator.generateCustomerVoicePlaceholderWithBindingInputParameters = function (surveyId, msfp_sourcesurveyidentifier, msfp_name, msfp_surveyurl, entityProviderName) {
        var bindingInputParameters = {
            surveyId: PlaceholderGenerator.generateBindingInputParameter({
                inputParameterValue: surveyId
            }),
            surveyFormId: PlaceholderGenerator.generateBindingInputParameter({
                inputParameterValue: msfp_sourcesurveyidentifier
            })
        };
        return PlaceholderGenerator.generatePlaceholderWithBindingInputParameters({
            title: msfp_name,
            placeholderType: CustomerVoicePlaceholderGenerator.stringType,
            entityProviderName: entityProviderName,
            entityAttributeLogicalName: CustomerVoicePlaceholderGenerator.outputPath,
            bindingInputParameters: bindingInputParameters,
            placeholderCategory: "general",
            isInternal: true,
            outputType: "unspecified",
            designerValue: msfp_surveyurl ? msfp_surveyurl : CustomerVoicePlaceholderGenerator.customerVoicePage
        });
    };
    CustomerVoicePlaceholderGenerator.outputPath = "surveyLink";
    CustomerVoicePlaceholderGenerator.customerVoicePage = "https://customervoice.microsoft.com";
    CustomerVoicePlaceholderGenerator.stringType = ["string"];
    return CustomerVoicePlaceholderGenerator;
}());
export { CustomerVoicePlaceholderGenerator };
var SurveyDto = /** @class */ (function () {
    function SurveyDto() {
    }
    return SurveyDto;
}());
